import React from "react";
import Nav from "../../component/Nav";
import Fab from "../../component/FAB";
import {
  IoIosWallet,
  IoIosCash,
  IoIosSearch,
  IoIosClose,
  IoIosArrowForward,
  IoIosArrowDown,
  IoMdAdd,
} from "react-icons/io";
import { useWallet, useWalletAmount } from "../../api";
import { FormatNumber } from "../../helpers";
import WalletLoader from "../../loader/WalletLoader";
import WalletCardEdit from "./WalletCardEdit";
import {
  classifyWallet,
  formatRelativeDate,
  mergeWalletStats,
  INACTIVE_DAYS,
} from "./walletGrouping";
import "./wallet.css";

export default function Wallet(props) {
  const { wallets, isLoading } = useWallet();
  const { walletAmounts } = useWalletAmount();
  const [search, setSearch] = React.useState("");
  const [showInactive, setShowInactive] = React.useState(false);

  const merged = React.useMemo(
    () => mergeWalletStats(wallets, walletAmounts),
    [wallets, walletAmounts]
  );

  const { active, inactive } = React.useMemo(() => {
    const a = [];
    const i = [];
    for (const w of merged) {
      if (classifyWallet(w) === "inactive") i.push(w);
      else a.push(w);
    }
    // Sort inactive: most recently-used first, never-used last
    i.sort((x, y) => {
      const dx = x.lastUsed ? new Date(x.lastUsed).getTime() : 0;
      const dy = y.lastUsed ? new Date(y.lastUsed).getTime() : 0;
      return dy - dx;
    });
    return { active: a, inactive: i };
  }, [merged]);

  const applySearch = (arr) => {
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        (w.description || "").toLowerCase().includes(q)
    );
  };

  const filteredActive = applySearch(active);
  const filteredInactive = applySearch(inactive);
  // When user searches, expand inactive automatically so they don't miss matches
  const inactiveExpanded = showInactive || (search.trim() && filteredInactive.length > 0);

  const totalBalance = merged.reduce((acc, w) => acc + (w.sum || 0), 0);
  const activeCount = active.filter((w) => (w.sum || 0) !== 0).length;
  const addWallet = () => props.history.push("/wallet/add");
  const editWallet = (w) => {
    localStorage.setItem("wallet", JSON.stringify(w));
    props.history.push("/wallet/edit");
  };

  if (isLoading) {
    return (
      <>
        <Nav title="Wallet" icon={IoIosWallet} />
        <div className="main-padding">
          <WalletLoader />
        </div>
      </>
    );
  }

  return (
    <>
      <Nav title="Wallet" icon={IoIosWallet} />
      <div className="main-padding mb-5">
        {/* ── Hero ──────────────────────────────────────────── */}
        <section className="wallet-hero">
          <div className="wallet-hero__label">Total Saldo Wallet</div>
          <div
            className={`wallet-hero__amount ${
              totalBalance < 0 ? "wallet-hero__amount--negative" : ""
            }`}
          >
            {totalBalance < 0 ? "−" : ""}Rp{" "}
            {FormatNumber(Math.abs(Math.round(totalBalance)))}
          </div>
          <div className="wallet-hero__sub">
            {wallets.length === 0
              ? "Belum ada wallet"
              : `${wallets.length} wallet · ${activeCount} aktif`}
          </div>
        </section>

        {/* ── Cash discovery tile ───────────────────────────── */}
        <div
          className="wallet-cash-tile"
          onClick={() => props.history.push("/cash")}
          role="button"
          tabIndex={0}
        >
          <span className="wallet-cash-tile__icon">
            <IoIosCash />
          </span>
          <div className="wallet-cash-tile__body">
            <div className="wallet-cash-tile__title">Catat uang tunai</div>
            <div className="wallet-cash-tile__desc">
              Cek isi dompet tanpa harus buka transaksi
            </div>
          </div>
          <IoIosArrowForward className="wallet-cash-tile__chevron" />
        </div>

        {/* ── Search ────────────────────────────────────────── */}
        {wallets.length > 0 && (
          <div className="wallet-search">
            <span className="wallet-search__icon">
              <IoIosSearch />
            </span>
            <input
              type="search"
              className="wallet-search__input"
              placeholder="Cari nama atau deskripsi wallet…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="wallet-search__clear"
                onClick={() => setSearch("")}
                aria-label="Hapus pencarian"
              >
                <IoIosClose />
              </button>
            )}
          </div>
        )}

        {/* ── Empty / placeholder / grouped lists ───────────── */}
        {wallets.length === 0 ? (
          <div className="wallet-empty">
            <IoIosWallet className="wallet-empty__icon" />
            <div className="wallet-empty__title">Belum ada wallet</div>
            <div className="wallet-empty__desc">
              Pisahkan dompet pribadi, bisnis, atau e-wallet. Tag tiap transaksi
              ke wallet yang sesuai supaya saldo tiap akun jelas.
            </div>
            <button
              type="button"
              className="wallet-empty__cta"
              onClick={addWallet}
            >
              Tambah wallet pertama →
            </button>
          </div>
        ) : filteredActive.length === 0 && filteredInactive.length === 0 ? (
          <div className="wallet-placeholder">
            Tidak ada wallet yang cocok dengan "{search}".
          </div>
        ) : (
          <>
            {filteredActive.length > 0 && (
              <>
                <div className="wallet-group-title">
                  AKTIF
                  <span className="wallet-group-count">
                    {filteredActive.length}
                  </span>
                </div>
                <ul className="wallet-list">
                  {filteredActive.map((w) => (
                    <WalletCardEdit
                      key={w.id}
                      data={w}
                      onClick={() => editWallet(w)}
                    />
                  ))}
                </ul>
              </>
            )}

            {inactive.length > 0 && (
              <>
                <button
                  type="button"
                  className={`wallet-group-toggle ${
                    inactiveExpanded ? "wallet-group-toggle--open" : ""
                  }`}
                  onClick={() => setShowInactive((v) => !v)}
                  aria-expanded={inactiveExpanded}
                >
                  <span className="wallet-group-toggle__label">
                    TIDAK AKTIF
                    <span className="wallet-group-count">
                      {filteredInactive.length}
                      {search && filteredInactive.length !== inactive.length
                        ? ` dari ${inactive.length}`
                        : ""}
                    </span>
                  </span>
                  <span className="wallet-group-toggle__hint">
                    Saldo nol & tidak ada transaksi {INACTIVE_DAYS} hari terakhir
                  </span>
                  <IoIosArrowDown className="wallet-group-toggle__chev" />
                </button>
                {inactiveExpanded && filteredInactive.length > 0 && (
                  <ul className="wallet-list wallet-list--inactive">
                    {filteredInactive.map((w) => (
                      <WalletCardEdit
                        key={w.id}
                        data={w}
                        inactive
                        lastUsedLabel={formatRelativeDate(w.lastUsed)}
                        onClick={() => editWallet(w)}
                      />
                    ))}
                  </ul>
                )}
                {inactiveExpanded &&
                  search &&
                  filteredInactive.length === 0 &&
                  inactive.length > 0 && (
                    <div className="wallet-placeholder">
                      Tidak ada wallet tidak aktif yang cocok dengan "{search}".
                    </div>
                  )}
              </>
            )}
          </>
        )}
      </div>
      <Fab icon={IoMdAdd} onClick={addWallet} />
    </>
  );
}
